<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useDashboardStore } from '@/stores/dashboard';
import { formatDate } from '@/utils/formatters';
import { getEntityTypeIcon } from '@/utils/kyc';
import BaseIcon from '@/components/base/BaseIcon.vue';
import LoadingState from '@/components/common/LoadingState.vue';
import Card from 'primevue/card';
import RecentActivityList from '@/components/dashboard/RecentActivityList.vue';
import RecentReportsList from '@/components/dashboard/RecentReportsList.vue';

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const dashboardStore = useDashboardStore();
const activeProfiles = computed(() => dashboardStore.activeProfiles);

onMounted(() => {
  dashboardStore.fetchDashboardData();
});
</script>

<template>
  <div class="p-4 sm:p-6 md:p-8">
    <header class="mb-8">
      <h1 class="text-3xl font-bold text-surface-800 mb-2">Bem-vindo de volta, {{ user?.first_name || 'Usuário' }}!</h1>
      <p class="text-lg text-surface-600">Aqui está uma visão geral rápida do seu ambiente de trabalho.</p>
    </header>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      <RouterLink to="/company-lookup" class="no-underline">
        <Card class="h-full hover:shadow-lg transition-shadow duration-300 rounded-lg">
          <template #title>
            <div class="flex items-center p-4">
              <BaseIcon class="text-4xl text-primary-500 mr-4">domain</BaseIcon>
              <h2 class="text-2xl font-semibold text-surface-800">Consulta de Empresas</h2>
            </div>
          </template>
          <template #content>
            <p class="px-4 pb-4 text-surface-600">Busque empresas e realize análises de risco.</p>
          </template>
        </Card>
      </RouterLink>
      <RouterLink to="/person-lookup" class="no-underline">
        <Card class="h-full hover:shadow-lg transition-shadow duration-300 rounded-lg">
          <template #title>
            <div class="flex items-center p-4">
              <BaseIcon class="text-4xl text-primary-500 mr-4">person</BaseIcon>
              <h2 class="text-2xl font-semibold text-surface-800">Consulta de Pessoas</h2>
            </div>
          </template>
          <template #content>
            <p class="px-4 pb-4 text-surface-600">Busque pessoas físicas e realize análises de risco.</p>
          </template>
        </Card>
      </RouterLink>
    </div>

    <!-- Recent Reports Section -->
    <div class="mb-8">
        <RecentReportsList title="Relatórios Recentes" :limit="5" />
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">

      <!-- Left Column -->
      <div class="lg:col-span-2">
        <RecentActivityList title="Atividade Recente" />
      </div>

      <!-- Right Column -->
      <div>
        <h2 class="text-2xl font-bold text-surface-800 mb-4">Políticas Ativas</h2>
        <div class="bg-white border border-surface-200 rounded-xl">
          <LoadingState v-if="!dashboardStore.hasLoaded" size="small" label="Carregando políticas…" />

          <ul v-else class="divide-y divide-surface-200">
                    <li v-for="profile in activeProfiles" :key="profile.id">
                        <RouterLink to="/account-admin/kyx" class="flex items-center !p-4 hover:bg-surface-50 transition-colors no-underline">
                            <div class="mr-4">
                                <BaseIcon class="text-2xl text-surface-500">{{ getEntityTypeIcon(profile.entity_type) }}</BaseIcon>
                            </div>
                            <div class="flex-grow">
                                <div class="font-semibold text-surface-800">{{ profile.profileName }}</div>
                                <div class="text-sm text-surface-500">
                                    <span>{{ profile.instances?.length || 0 }} versions</span>
                                </div>
                            </div>
                            <div class="text-xs text-surface-500 text-right">
                                <div v-if="profile.createdAt">Criado em: {{ formatDate(profile.createdAt) }}</div>
                                <div v-if="profile.last_updated_at">Atualizado em: {{ formatDate(profile.last_updated_at) }}</div>
                            </div>
                        </RouterLink>
                    </li>
          </ul>
          <div v-if="dashboardStore.hasLoaded && !activeProfiles?.length"
            class="text-center py-12 text-surface-500">
            <div class="w-16 h-16 rounded-full bg-surface-100 flex items-center justify-center mx-auto mb-4">
              <BaseIcon class="text-3xl text-surface-400">shield</BaseIcon>
            </div>
            <h3 class="text-lg font-semibold text-surface-700">Ainda não há políticas ativas</h3>
            <p class="text-sm text-surface-500">Crie novas políticas na área administrativa.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-underline {
  text-decoration: none;
}
</style>
